import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import {
    collection, getDocs, addDoc, updateDoc, deleteDoc,
    doc, serverTimestamp, orderBy, query, limit
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiFileText } from "react-icons/fi";
import { compressImage } from "../../utils/imageUtils";
import toast from "react-hot-toast";

const EMPTY_BLOG = {
    title: "", excerpt: "", content: "", imageUrl: "", published: true,
    date: new Date().toISOString().split('T')[0],
    imageSize: "medium",
    suggestedProductId: ""
};

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_BLOG);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [allProducts, setAllProducts] = useState([]);

    async function loadProducts() {
        try {
            const snap = await getDocs(collection(db, "products"));
            setAllProducts(snap.docs.map(d => ({ id: d.id, title: d.data().title })));
        } catch (e) { console.error(e); }
    }

    async function loadPosts() {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "blog"));
            const allPosts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            const sorted = allPosts.sort((a, b) => {
                const dateA = a.date ? new Date(a.date + "T00:00:00") : (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0));
                const dateB = b.date ? new Date(b.date + "T00:00:00") : (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0));
                return dateB - dateA;
            });

            setPosts(sorted);
        } catch (e) {
            toast.error("Could not load blog posts.");
        }
        setLoading(false);
    }

    useEffect(() => { 
        loadPosts(); 
        loadProducts();
    }, []);

    function openAdd() { setForm(EMPTY_BLOG); setEditId(null); setModal(true); }
    function openEdit(p) {
        setForm({
            title: p.title || "", excerpt: p.excerpt || "",
            content: p.content || "", imageUrl: p.imageUrl || "",
            published: p.published ?? true,
            date: p.date || (p.createdAt?.toDate ? p.createdAt.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
            imageSize: p.imageSize || "medium",
            suggestedProductId: p.suggestedProductId || ""
        });
        setEditId(p.id);
        setModal(true);
    }
    function closeModal() { setModal(false); setEditId(null); }

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    async function handleImageUpload(e) {
        let file = e.target.files[0];
        if (!file) return;
        
        setUploading(true);
        try {
            // Compress image
            file = await compressImage(file);
            
            const storageRef = ref(storage, `blog/${Date.now()}_${file.name}`);
            const task = uploadBytesResumable(storageRef, file);
            
            task.on("state_changed", null, 
                (error) => { 
                    console.error("Upload error:", error);
                    toast.error("Upload failed."); 
                    setUploading(false); 
                }, 
                async () => {
                    const url = await getDownloadURL(task.snapshot.ref);
                    setForm(f => ({ ...f, imageUrl: url }));
                    setUploading(false);
                    toast.success("Blog image uploaded!");
                }
            );
        } catch (e) {
            toast.error("Compression failed.");
            setUploading(false);
        }
    }

    async function handleSave() {
        if (!form.title || !form.content) { toast.error("Title and content are required."); return; }
        setSaving(true);
        try {
            const payload = { ...form, updatedAt: serverTimestamp() };
            if (editId) {
                await updateDoc(doc(db, "blog", editId), payload);
                toast.success("Post updated!");
            } else {
                await addDoc(collection(db, "blog"), { ...payload, createdAt: serverTimestamp() });
                toast.success("Post published!");
            }
            closeModal();
            loadPosts();
        } catch {
            toast.error("Failed to save post.");
        }
        setSaving(false);
    }

    async function handleDelete(id) {
        if (!window.confirm("Delete this post?")) return;
        try {
            await deleteDoc(doc(db, "blog", id));
            toast.success("Post deleted.");
            loadPosts();
        } catch {
            toast.error("Failed to delete.");
        }
    }

    return (
        <div>
            <div className="admin-section-header">
                <h3>Blog Entries</h3>
                <button className="admin-btn-primary" onClick={openAdd}>
                    <FiPlus size={16} /> New Entry
                </button>
            </div>

            <div className="admin-card">
                {loading ? <div className="admin-loading">Loading blog…</div> : (
                    <div className="admin-blog-list">
                        {posts.map(p => (
                            <div key={p.id} className="admin-blog-item">
                                <div className="blog-item-img">
                                    {p.imageUrl ? <img src={p.imageUrl} alt="" /> : <div className="img-placeholder"><FiFileText size={24} /></div>}
                                </div>
                                <div className="blog-item-info">
                                    <h4>{p.title}</h4>
                                    <p>{p.excerpt || "No excerpt provided..."}</p>
                                    <div className="blog-item-meta">
                                        <span>{p.published ? "Published" : "Draft"}</span>
                                    </div>
                                </div>
                                <div className="blog-item-actions">
                                    <button className="admin-btn-secondary" onClick={() => openEdit(p)}><FiEdit2 size={14} /></button>
                                    <button className="admin-btn-danger" onClick={() => handleDelete(p.id)}><FiTrash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {modal && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && closeModal()}>
                    <div className="admin-modal" style={{ maxWidth: 800 }}>
                        <div className="admin-modal-header">
                            <h3>{editId ? "Edit Post" : "New Blog Entry"}</h3>
                            <button className="admin-modal-close" onClick={closeModal}><FiX size={20} /></button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form">
                                <div className="admin-form-grid">
                                    <div className="admin-form-field full">
                                        <label>Post Title *</label>
                                        <input name="title" value={form.title} onChange={handleChange} placeholder="Exciting news about..." />
                                    </div>
                                    <div className="admin-form-field full">
                                        <label>Short Excerpt</label>
                                        <input name="excerpt" value={form.excerpt} onChange={handleChange} placeholder="A quick summary for the preview card..." />
                                    </div>
                                    <div className="admin-form-field full">
                                        <label>Content *</label>
                                        <ReactQuill theme="snow" value={form.content} onChange={v => setForm(f => ({ ...f, content: v }))} style={{ height: 300, marginBottom: 50 }} />
                                    </div>
                                    <div className="admin-form-field full">
                                        <label>Featured Image</label>
                                        {form.imageUrl && (
                                            <div style={{ position: "relative", marginBottom: 12 }}>
                                                <img src={form.imageUrl} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8 }} />
                                                <button 
                                                    onClick={(e) => { e.preventDefault(); setForm(f => ({ ...f, imageUrl: "" })); }}
                                                    style={{
                                                        position: "absolute", top: 10, right: 10,
                                                        background: "rgba(231, 76, 60, 0.9)", color: "white",
                                                        border: "none", borderRadius: "50%", width: 28, height: 28,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                                                    }}
                                                    title="Remove Image"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                        <label className="admin-upload-label">
                                            <FiUpload size={16} /> {uploading ? "Uploading..." : form.imageUrl ? "Change Header Image" : "Upload Header Image"}
                                            <input type="file" onChange={handleImageUpload} hidden />
                                        </label>
                                    </div>
                                    <div className="admin-form-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                                        <input type="checkbox" name="published" checked={form.published} onChange={handleChange} id="published" />
                                        <label htmlFor="published">Visible on Website</label>
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Publish Date</label>
                                        <input type="date" name="date" value={form.date} onChange={handleChange} />
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Header Image Size</label>
                                        <select name="imageSize" value={form.imageSize} onChange={handleChange}>
                                            <option value="small">Small (30% Viewport)</option>
                                            <option value="medium">Medium (50% Viewport)</option>
                                            <option value="large">Large (80% Viewport)</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-field full">
                                        <label>Suggest / Feature a Product</label>
                                        <select name="suggestedProductId" value={form.suggestedProductId} onChange={handleChange}>
                                            <option value="">-- None --</option>
                                            {allProducts.map(p => (
                                                <option key={p.id} value={p.id}>{p.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn-secondary" onClick={closeModal}>Cancel</button>
                            <button className="admin-btn-primary" onClick={handleSave} disabled={saving || uploading}>{saving ? "Saving..." : "Save Entry"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
